import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Input,
  IconButton,
  Table,
  Button,
  Chip,
  Sheet,
  Select,
  Option,
} from '@mui/joy';
import {
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Pencil,
  Trash2,
  CircleDot,
  Clock3,
  CheckCircle2,
  Plus,
  Download,
  FileSpreadsheet,
} from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ModaleConferma from '../../components/ModaleConferma';
import ModaleIssue from '../../components/ModaleIssue';
import * as XLSX from 'xlsx';
// importa qui il tuo controllo permessi corretto se lo hai già
// import { canEditIssues } from '../../utils/permissions';

export default function Issues() {
  const navigate = useNavigate();
  const selectedOrg = localStorage.getItem('selectedOrg');

  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('all');

  const [page, setPage] = useState(1);
  const perPage = 20;

  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc',
  });

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [issueToDelete, setIssueToDelete] = useState(null);
  const [showModale, setShowModale] = useState(false);

  // Sostituisci con la tua utility permessi se esiste
  const canEdit = true;

  const stateLabels = {
    open: {
      label: 'Aperta',
      color: 'danger',
      icon: <CircleDot size={16} />,
    },
    in_progress: {
      label: 'In lavorazione',
      color: 'warning',
      icon: <Clock3 size={16} />,
    },
    closed: {
      label: 'Chiusa',
      color: 'success',
      icon: <CheckCircle2 size={16} />,
    },
  };

  const fetchIssues = useCallback(async () => {
    if (!selectedOrg) return;

    setLoading(true);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/issue/all`,
        { credentials: 'include' }
      );

      if (!res.ok) throw new Error('Errore fetch issue');

      const data = await res.json();
      setIssues(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Errore nel fetch issue:', err);
      setIssues([]);
      toast.error('Errore nel caricamento delle issue', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [selectedOrg]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d)) return '';
    return `${d.toLocaleDateString('it-IT')} ${d.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  const normalize = (v) => (v ?? '').toString().toLowerCase();

  const filteredIssues = useMemo(() => {
    const term = normalize(searchTerm);

    return issues
      .filter((i) => (stateFilter === 'all' ? true : i.state === stateFilter))
      .filter((i) => {
        if (!term) return true;

        return (
          normalize(i.subject).includes(term) ||
          normalize(i.description).includes(term) ||
          normalize(i.resolution).includes(term) ||
          normalize(i.vehicle_name).includes(term) ||
          normalize(i.created_by_name).includes(term) ||
          normalize(i.closed_by_name).includes(term) ||
          normalize(i.state).includes(term)
        );
      });
  }, [issues, searchTerm, stateFilter]);

  const sortedIssues = useMemo(() => {
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;

    const getComparable = (item) => {
      const val = item?.[key];

      if (key === 'id' || key === 'vehicle_id') {
        return Number(val) || 0;
      }

      if (key === 'created_at' || key === 'updated_at') {
        const t = val ? new Date(val).getTime() : 0;
        return Number.isFinite(t) ? t : 0;
      }

      return normalize(val);
    };

    return [...filteredIssues].sort((a, b) => {
      const A = getComparable(a);
      const B = getComparable(b);
      if (A < B) return -1 * dir;
      if (A > B) return 1 * dir;
      return 0;
    });
  }, [filteredIssues, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedIssues.length / perPage));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedIssues = useMemo(() => {
    const start = (page - 1) * perPage;
    return sortedIssues.slice(start, start + perPage);
  }, [page, sortedIssues]);

  const handleSort = (key) => {
    setPage(1);
    setSortConfig((cur) => {
      if (cur.key === key) {
        return { key, direction: cur.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowDown size={14} color="#bbb" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUp size={14} color="black" />
      : <ArrowDown size={14} color="black" />;
  };

  const handleDeleteIssue = async () => {
    const issueId = issueToDelete?.id;
    if (!issueId) return;

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/issue/${issueId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!res.ok) throw new Error('Errore eliminazione issue');

      toast.success('Issue eliminata con successo', {
        position: 'top-right',
        autoClose: 2500,
      });

      fetchIssues();
    } catch (err) {
      console.error(err);
      toast.error('Errore nell’eliminazione della issue', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setConfirmDeleteOpen(false);
      setIssueToDelete(null);
    }
  };

  const getExportRows = () => {
    return sortedIssues.map((issue) => ({
      ID: issue.id ?? '',
      Oggetto: issue.subject ?? '',
      Descrizione: issue.description ?? '',
      Risoluzione: issue.resolution ?? '',
      Mezzo: issue.vehicle_name ?? '',
      'Aperta da': issue.created_by_name ?? '',
      'Chiusa da': issue.closed_by_name ?? '',
      Stato:
        issue.state === 'open'
          ? 'Aperta'
          : issue.state === 'in_progress'
          ? 'In lavorazione'
          : issue.state === 'closed'
          ? 'Chiusa'
          : issue.state ?? '',
      'Creata il': formatDate(issue.created_at),
      'Aggiornata il': formatDate(issue.updated_at),
    }));
  };

  const getTimestampForFile = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');

    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}`;
  };

  const exportCSV = () => {
    try {
      const rows = getExportRows();

      if (!rows.length) {
        toast.info('Nessun dato da esportare', {
          position: 'top-right',
          autoClose: 2500,
        });
        return;
      }

      const headers = Object.keys(rows[0]);

      const escapeCsvValue = (value) => {
        const stringValue = value == null ? '' : String(value);
        const escaped = stringValue.replace(/"/g, '""');
        return `"${escaped}"`;
      };

      const csvContent = [
        headers.join(';'),
        ...rows.map((row) => headers.map((header) => escapeCsvValue(row[header])).join(';')),
      ].join('\n');

      const blob = new Blob([`\uFEFF${csvContent}`], {
        type: 'text/csv;charset=utf-8;',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `issue_filtrate_${getTimestampForFile()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('CSV esportato con successo', {
        position: 'top-right',
        autoClose: 2500,
      });
    } catch (err) {
      console.error('Errore export CSV:', err);
      toast.error('Errore durante l’esportazione CSV', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const exportExcel = () => {
    try {
      const rows = getExportRows();

      if (!rows.length) {
        toast.info('Nessun dato da esportare', {
          position: 'top-right',
          autoClose: 2500,
        });
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Issues');

      XLSX.writeFile(workbook, `issue_filtrate_${getTimestampForFile()}.xlsx`);

      toast.success('Excel esportato con successo', {
        position: 'top-right',
        autoClose: 2500,
      });
    } catch (err) {
      console.error('Errore export Excel:', err);
      toast.error('Errore durante l’esportazione Excel', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  return (
    <Box>
      <Typography level="h3" mb={2}>
        Problemi
      </Typography>

      <Sheet
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 'lg',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Input
            placeholder="Cerca (oggetto, descrizione, mezzo, utente)..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            sx={{ width: { xs: '100%', sm: 360 } }}
          />

          <Select
            value={stateFilter}
            onChange={(_, value) => {
              setStateFilter(value || 'all');
              setPage(1);
            }}
            sx={{ minWidth: 180 }}
          >
            <Option value="all">Tutti gli stati</Option>
            <Option value="open">Aperte</Option>
            <Option value="in_progress">In lavorazione</Option>
            <Option value="closed">Chiuse</Option>
          </Select>

          <Typography level="body-sm" color="neutral">
            {sortedIssues.length} record trovati
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<Download size={16} />}
            onClick={exportCSV}
            disabled={loading || sortedIssues.length === 0}
          >
            Esporta CSV
          </Button>

          <Button
            variant="outlined"
            color="success"
            startDecorator={<FileSpreadsheet size={16} />}
            onClick={exportExcel}
            disabled={loading || sortedIssues.length === 0}
          >
            Esporta Excel
          </Button>

          {canEdit && (
            <Button
              variant="solid"
              color="primary"
              startDecorator={<Plus size={16} />}
              onClick={() => setShowModale(true)}
            >
              Nuovo Problema
            </Button>
          )}
        </Box>
      </Sheet>

      {loading ? (
        <Typography>Caricamento...</Typography>
      ) : sortedIssues.length === 0 ? (
        <Typography>Nessun problema trovato.</Typography>
      ) : (
        <Box
          sx={{
            borderRadius: 'lg',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'neutral.outlinedBorder',
          }}
        >
          <Table
            size="sm"
            sx={{
              minWidth: { xs: '100%', sm: 900 },
              tableLayout: 'auto',
              '& thead th': {
                bgcolor: 'neutral.softBg',
                textAlign: 'left',
                px: { xs: 1, sm: 2 },
                py: { xs: 0.75, sm: 1 },
                fontSize: { xs: 'xs', sm: 'sm' },
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              },
              '& tbody td': {
                px: { xs: 1, sm: 2 },
                py: { xs: 0.75, sm: 1 },
                fontSize: { xs: 'sm', sm: 'md' },
                lineHeight: 1.2,
                verticalAlign: 'middle',
              },
              '& tbody tr:hover': {
                bgcolor: 'neutral.plainHoverBg',
              },
            }}
          >
            <thead>
              <tr>
                <th onClick={() => handleSort('subject')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Oggetto <SortIcon columnKey="subject" />
                  </Box>
                </th>

                <th onClick={() => handleSort('vehicle_name')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Mezzo <SortIcon columnKey="vehicle_name" />
                  </Box>
                </th>

                <th onClick={() => handleSort('created_by_name')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Aperta da <SortIcon columnKey="created_by_name" />
                  </Box>
                </th>

                <th onClick={() => handleSort('closed_by_name')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Chiusa da <SortIcon columnKey="closed_by_name" />
                  </Box>
                </th>

                <th onClick={() => handleSort('state')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Stato <SortIcon columnKey="state" />
                  </Box>
                </th>

                <th onClick={() => handleSort('created_at')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Creata il <SortIcon columnKey="created_at" />
                  </Box>
                </th>

                <th onClick={() => handleSort('updated_at')}>
                  <Box display="flex" alignItems="center" gap={1}>
                    Aggiornata il <SortIcon columnKey="updated_at" />
                  </Box>
                </th>

                {canEdit && (
                  <th style={{ textAlign: 'center', width: 120, cursor: 'default' }}>
                    Azioni
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {paginatedIssues.map((issue) => {
                const state = stateLabels[issue.state];

                return (
                  <tr key={issue.id}>
                    <td>
                      <Box
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/problemi/${issue.id}`)}
                        title="Apri dettaglio"
                      >
                        <Typography level="body-sm" fontWeight="lg">
                          {issue.subject}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {issue.description
                            ? issue.description.length > 80
                              ? `${issue.description.slice(0, 80)}...`
                              : issue.description
                            : '—'}
                        </Typography>
                      </Box>
                    </td>

                    <td>{issue.vehicle_name || '—'}</td>
                    <td>{issue.created_by_name || '—'}</td>
                    <td>{issue.closed_by_name || '—'}</td>

                    <td>
                      {state ? (
                        <Chip
                          variant="soft"
                          color={state.color}
                          startDecorator={state.icon}
                          size="sm"
                        >
                          {state.label}
                        </Chip>
                      ) : (
                        <Typography level="body-sm" color="neutral">
                          —
                        </Typography>
                      )}
                    </td>

                    <td>{formatDate(issue.created_at) || '—'}</td>
                    <td>{formatDate(issue.updated_at) || '—'}</td>

                    {canEdit && (
                      <td style={{ textAlign: 'center' }}>
                        <Box display="flex" justifyContent="center" gap={1}>
                          <IconButton
                            size="sm"
                            variant="soft"
                            color="primary"
                            onClick={() => navigate(`/problemi/${issue.id}`)}
                          >
                            <Pencil size={16} />
                          </IconButton>

                          <IconButton
                            size="sm"
                            variant="soft"
                            color="danger"
                            onClick={() => {
                              setIssueToDelete(issue);
                              setConfirmDeleteOpen(true);
                            }}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Box>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Box>
      )}

      {totalPages > 1 && (
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Button
            size="sm"
            variant="outlined"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            startDecorator={<ChevronLeft size={14} />}
          >
            Precedente
          </Button>

          <Typography level="body-sm">
            Pagina {page} di {totalPages}
          </Typography>

          <Button
            size="sm"
            variant="outlined"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            endDecorator={<ChevronRight size={14} />}
          >
            Successiva
          </Button>
        </Box>
      )}

      <ModaleIssue
        open={showModale}
        onClose={() => setShowModale(false)}
        onSuccess={() => {
          fetchIssues();
          setShowModale(false);
          toast.success('Issue creata con successo', {
            position: 'top-right',
            autoClose: 2500,
          });
        }}
      />

      <ModaleConferma
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteIssue}
        title="Conferma eliminazione"
        message={
          <>
            Sei sicuro di voler eliminare il problema <b>{issueToDelete?.subject || 'selezionata'}</b>?
          </>
        }
      />
    </Box>
  );
}